/**
 * User Olo Tab Component
 *
 * Displays Olo account information and allows creating/refreshing SSO link.
 * Shows status, Olo ID, and per-platform auth tokens with masking.
 * Supports refreshing individual platform tokens or all platforms at once.
 */

import { useState } from 'react';
import { Copy, RefreshCw, Eye, EyeOff, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useCreateOloLink } from '@/hooks/use-user-management';
import type { User } from '@/types/user-management';

interface UserOloTabProps {
  user: User;
}

/**
 * Platform type for token operations
 */
type Platform = 'ios' | 'android' | 'web' | 'backend';

/**
 * Platform token info for display
 */
interface PlatformTokenInfo {
  label: string;
  platform: Platform;
  token: string | null;
}

/**
 * Platform display labels for proper casing (e.g., "iOS" not "IOS")
 */
const PLATFORM_LABELS: Record<Platform, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  backend: 'Backend',
};

/**
 * User Olo Tab
 * Displays Olo account information and SSO link management
 */
export function UserOloTab({ user }: UserOloTabProps) {
  const { toast } = useToast();
  const [showTokens, setShowTokens] = useState<Record<string, boolean>>({});
  const [copiedTokens, setCopiedTokens] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [refreshingPlatform, setRefreshingPlatform] = useState<Platform | null>(null);

  const createOloLinkMutation = useCreateOloLink();

  const hasOloAccount = !!user.oloId;

  // Check if user has any Olo auth token across platforms
  const hasAnyOloAuthToken = !!(
    user.oloAuthTokenIos ||
    user.oloAuthTokenAndroid ||
    user.oloAuthTokenWeb ||
    user.oloAuthTokenBackend
  );

  // Platform tokens for display
  const platformTokens: PlatformTokenInfo[] = [
    { label: 'iOS', platform: 'ios', token: user.oloAuthTokenIos },
    { label: 'Android', platform: 'android', token: user.oloAuthTokenAndroid },
    { label: 'Web', platform: 'web', token: user.oloAuthTokenWeb },
    { label: 'Backend', platform: 'backend', token: user.oloAuthTokenBackend },
  ];

  /**
   * Handle Copy to Clipboard
   */
  const handleCopy = async (value: string, label: string, key?: string) => {
    try {
      await navigator.clipboard.writeText(value);
      if (key) {
        setCopiedTokens(prev => ({ ...prev, [key]: true }));
        setTimeout(() => setCopiedTokens(prev => ({ ...prev, [key]: false })), 2000);
      }
      toast({
        title: 'Copied',
        description: `${label} copied to clipboard`,
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  /**
   * Toggle token visibility for a specific platform
   */
  const toggleTokenVisibility = (platform: string) => {
    setShowTokens(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  /**
   * Handle refreshing a single platform token
   */
  const handleRefreshPlatform = async (platform: Platform) => {
    setRefreshingPlatform(platform);
    try {
      const result = await createOloLinkMutation.mutateAsync({
        userId: user.id,
        platforms: [platform],
      });

      // Check for platform-specific success/failure
      const platformResult = result.platformResults?.[platform];
      const platformLabel = PLATFORM_LABELS[platform];
      if (platformResult?.success) {
        toast({
          title: 'Success',
          description: `${platformLabel} token refreshed successfully`,
        });
      } else {
        toast({
          title: 'Error',
          description: platformResult?.error || `Failed to refresh ${platformLabel} token`,
          variant: 'destructive',
        });
      }
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : `Failed to refresh ${PLATFORM_LABELS[platform]} token`,
        variant: 'destructive',
      });
    } finally {
      setRefreshingPlatform(null);
    }
  };

  /**
   * Handle Create/Refresh Olo Link (all platforms)
   */
  const handleCreateOloLink = async () => {
    try {
      const result = await createOloLinkMutation.mutateAsync({ userId: user.id });

      // Check for partial failures
      if (result.platformResults) {
        const failures = Object.entries(result.platformResults).filter(([, r]) => !r.success);
        if (failures.length > 0) {
          const failedPlatforms = failures
            .map(([p]) => PLATFORM_LABELS[p as Platform] ?? p)
            .join(', ');
          toast({
            title: 'Partial Success',
            description: `Some platforms failed to refresh: ${failedPlatforms}`,
            variant: 'default',
          });
        } else {
          toast({
            title: 'Success',
            description: hasOloAccount
              ? 'All platform tokens have been refreshed successfully'
              : 'Olo SSO link has been created successfully',
          });
        }
      } else {
        toast({
          title: 'Success',
          description: hasOloAccount
            ? 'Olo SSO link has been refreshed successfully'
            : 'Olo SSO link has been created successfully',
        });
      }
      setIsDialogOpen(false);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create Olo link',
        variant: 'destructive',
      });
    }
  };

  /**
   * Check if a specific platform is currently being refreshed
   */
  const isPlatformRefreshing = (platform: Platform) => {
    return refreshingPlatform === platform;
  };

  /**
   * Check if any operation is in progress
   */
  const isAnyOperationPending = createOloLinkMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Olo Account</h3>
        <Badge variant={hasOloAccount ? 'success' : 'secondary'}>
          {hasOloAccount ? 'Linked' : 'Not Linked'}
        </Badge>
      </div>

      {/* Account Information */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold mb-2">Account Information</h4>
        <div className="rounded-lg border p-4 space-y-3">
          {/* Olo ID */}
          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Olo ID</p>
              <p className="text-sm font-mono mt-1">{user.oloId || 'N/A'}</p>
            </div>
            {user.oloId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  handleCopy(user.oloId!, 'Olo ID', 'oloId');
                  setCopiedId(true);
                  setTimeout(() => setCopiedId(false), 2000);
                }}
                disabled={copiedId}
                aria-label="Copy Olo ID"
              >
                {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>

          {/* Platform-Specific Auth Tokens */}
          <div className="py-2">
            <p className="text-sm font-medium text-muted-foreground mb-3">
              Auth Tokens by Platform
              {hasAnyOloAuthToken && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {platformTokens.filter(t => t.token).length} active
                </Badge>
              )}
            </p>
            <div className="space-y-2">
              {platformTokens.map(({ label, platform, token }) => {
                const maskedToken = token ? '*'.repeat(Math.min(token.length, 20)) + '...' : 'N/A';
                const isVisible = showTokens[platform];
                const isCopied = copiedTokens[platform];
                const isRefreshing = isPlatformRefreshing(platform);

                return (
                  <div
                    key={platform}
                    className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-md"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-muted-foreground">{label}</p>
                      <p className="text-sm font-mono mt-1 break-all truncate">
                        {token ? (isVisible ? token : maskedToken) : 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {token && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleTokenVisibility(platform)}
                            aria-label={isVisible ? 'Hide token' : 'Show token'}
                            className="h-8 w-8 p-0"
                          >
                            {isVisible ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(token, `${label} Auth Token`, platform)}
                            disabled={isCopied}
                            aria-label={`Copy ${label} auth token`}
                            className="h-8 w-8 p-0"
                          >
                            {isCopied ? (
                              <Check className="h-3.5 w-3.5" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </>
                      )}
                      {/* Per-platform refresh button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRefreshPlatform(platform)}
                        disabled={isAnyOperationPending}
                        aria-label={`Refresh ${label} token`}
                        className="h-8 w-8 p-0"
                        title={`Refresh ${label} token`}
                      >
                        <RefreshCw
                          className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
                        />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">Actions</h4>
        <Button
          onClick={() => setIsDialogOpen(true)}
          disabled={isAnyOperationPending}
          className="w-full"
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isAnyOperationPending && !refreshingPlatform ? 'animate-spin' : ''}`}
          />
          {hasOloAccount ? 'Refresh All Platforms' : 'Create SSO Link'}
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {hasOloAccount ? 'Refresh All Platform Tokens' : 'Create Olo SSO Link'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {hasOloAccount
                ? 'This will refresh the Olo SSO link for all platforms (iOS, Android, Web, Backend). All auth tokens will be updated.'
                : 'This will create an Olo SSO link for this user and generate auth tokens for all platforms. A new Olo account will be created if needed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAnyOperationPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreateOloLink} disabled={isAnyOperationPending}>
              {isAnyOperationPending
                ? 'Processing...'
                : hasOloAccount
                  ? 'Refresh All'
                  : 'Create Link'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
